<template>
  <div class="ov-page">
    <div class="ov-header">
      <div class="ov-note ov-note--info ov-header__note">
        <v-icon small>mdi-information-outline</v-icon>
        <span>{{ $t('users.createSubtitle') }}. {{ $t('users.requiredFields') }}</span>
      </div>
    </div>

    <div class="cau-grid">
      <!-- ===== FORMULAIRE ===== -->
      <div class="ov-card">
        <div class="ov-card__head">
          <div>
            <div class="ov-card__kicker">{{ editingIndex >= 0 ? $t('users.updateAccountBtn') : $t('users.addAccountBtn') }}</div>
            <div class="ov-card__title">{{ $t('users.accountInfo') }}</div>
          </div>
        </div>

        <div class="ov-card__body">
          <!-- Identité -->
          <div class="cau-section">
            <div class="cau-section__label">{{ $t('users.sectionIdentity') }}</div>
            <div class="ov-row">
              <div class="ov-field">
                <label class="ov-label">{{ $t('users.lastName') }}<span class="ov-required">*</span></label>
                <input
                  v-model="userAccount.nom"
                  type="text"
                  class="ov-input"
                  :class="{ 'is-invalid': submitted && !userAccount.nom }"
                  :placeholder="$t('users.lastNamePlaceholder')"
                />
                <div v-if="submitted && !userAccount.nom" class="ov-error">Champ obligatoire</div>
              </div>
              <div class="ov-field">
                <label class="ov-label">{{ $t('users.firstName') }}<span class="ov-required">*</span></label>
                <input
                  v-model="userAccount.prenoms"
                  type="text"
                  class="ov-input"
                  :class="{ 'is-invalid': submitted && !userAccount.prenoms }"
                  :placeholder="$t('users.firstNamePlaceholder')"
                />
                <div v-if="submitted && !userAccount.prenoms" class="ov-error">Champ obligatoire</div>
              </div>
            </div>
          </div>

          <!-- Connexion -->
          <div class="cau-section">
            <div class="cau-section__label">{{ $t('users.sectionConnection') }}</div>
            <div class="cau-stack">
              <div class="ov-field">
                <label class="ov-label">{{ $t('users.login') }}<span class="ov-required">*</span></label>
                <input
                  v-model="userAccount.login"
                  type="text"
                  class="ov-input"
                  :class="{ 'is-invalid': submitted && !userAccount.login }"
                  :placeholder="$t('users.loginPlaceholder')"
                  autocomplete="off"
                />
                <div v-if="submitted && !userAccount.login" class="ov-error">Champ obligatoire</div>
              </div>

              <div class="ov-field">
                <label class="ov-label">{{ $t('users.email') }}<span class="ov-required">*</span></label>
                <input
                  v-model="userAccount.email"
                  type="email"
                  class="ov-input"
                  :class="{ 'is-invalid': submitted && emailError }"
                  :placeholder="$t('users.emailPlaceholder')"
                  autocomplete="off"
                />
                <div v-if="submitted && emailError" class="ov-error">{{ emailError }}</div>
              </div>

              <div class="ov-field">
                <label class="ov-label">{{ $t('users.password') }}<span class="ov-required">*</span></label>
                <div class="cau-password">
                  <input
                    v-model="userAccount.password"
                    :type="passwordIsVisible ? 'text' : 'password'"
                    class="ov-input"
                    :class="{ 'is-invalid': submitted && !userAccount.password }"
                    :placeholder="$t('users.passwordPlaceholder')"
                    autocomplete="new-password"
                  />
                  <button type="button" class="cau-password__toggle" @click="passwordIsVisible = !passwordIsVisible">
                    <v-icon small>{{ passwordIsVisible ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
                  </button>
                </div>
                <div v-if="submitted && !userAccount.password" class="ov-error">Champ obligatoire</div>
              </div>

              <div class="ov-field">
                <label class="ov-label">{{ $t('users.confirmPassword') }}<span class="ov-required">*</span></label>
                <div class="cau-password">
                  <input
                    v-model="confirmPassword"
                    :type="confirmPasswordIsVisible ? 'text' : 'password'"
                    class="ov-input"
                    :class="{ 'is-invalid': submitted && confirmPasswordError }"
                    :placeholder="$t('users.confirmPasswordPlaceholder')"
                    autocomplete="new-password"
                  />
                  <button type="button" class="cau-password__toggle" @click="confirmPasswordIsVisible = !confirmPasswordIsVisible">
                    <v-icon small>{{ confirmPasswordIsVisible ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
                  </button>
                </div>
                <div v-if="submitted && confirmPasswordError" class="ov-error">{{ confirmPasswordError }}</div>
              </div>
            </div>
          </div>

          <!-- Rôle & affectation -->
          <div class="cau-section">
            <div class="cau-section__label">{{ $t('users.sectionRole') }}</div>
            <div class="cau-stack">
              <div class="ov-field">
                <label class="ov-label">{{ $t('users.userRole') }}<span class="ov-required">*</span></label>
                <select
                  v-model="userRoleCode"
                  class="ov-select"
                  :class="{ 'is-invalid': submitted && !userRoleCode }"
                >
                  <option :value="null" disabled>{{ $t('users.selectRole') }}</option>
                  <option v-for="role in userRoleToShow" :key="role.code" :value="role.code">{{ role.libelle }}</option>
                </select>
                <div v-if="submitted && !userRoleCode" class="ov-error">{{ $t('users.selectRoleError') }}</div>
              </div>

              <div v-if="showCompagnieField" class="ov-field">
                <label class="ov-label">{{ $t('users.transportCompany') }}<span class="ov-required">*</span></label>
                <select
                  v-model="compagnieTransportRaisonSociale"
                  class="ov-select"
                  :class="{ 'is-invalid': submitted && !compagnieTransportRaisonSociale }"
                  @change="getAllStations(compagnieTransportRaisonSociale)"
                >
                  <option :value="null" disabled>{{ $t('users.selectCompany') }}</option>
                  <option v-for="c in compagnieTransportList" :key="c.raisonSociale" :value="c.raisonSociale">
                    {{ c.raisonSociale }}
                  </option>
                </select>
                <div v-if="submitted && !compagnieTransportRaisonSociale" class="ov-error">{{ $t('users.selectCompanyError') }}</div>
              </div>

              <div v-if="showGareField" class="ov-field">
                <label class="ov-label">{{ $t('users.transportStation') }}<span class="ov-required">*</span></label>
                <select
                  v-model="gareTransport"
                  class="ov-select"
                  :class="{ 'is-invalid': submitted && !gareTransport }"
                >
                  <option :value="null" disabled>{{ $t('users.selectStation') }}</option>
                  <option v-for="s in stations" :key="s.designation" :value="s.designation">{{ s.designation }}</option>
                </select>
                <div v-if="submitted && !gareTransport" class="ov-error">{{ $t('users.selectStationError') }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="ov-card__foot">
          <button type="button" class="ov-btn ov-btn--secondary" @click="reinintialiseForm">
            {{ $t('users.cancel') }}
          </button>
          <button type="button" class="ov-btn ov-btn--navy" @click="addToList">
            <v-icon small>{{ editingIndex >= 0 ? 'mdi-check' : 'mdi-plus' }}</v-icon>
            {{ editingIndex >= 0 ? $t('users.updateAccountBtn') : $t('users.addAccountBtn') }}
          </button>
        </div>
      </div>

      <!-- ===== COMPTES DE LA SESSION ===== -->
      <div class="ov-card cau-list">
        <div class="ov-card__head">
          <div>
            <div class="ov-card__kicker">En attente de création</div>
            <div class="ov-card__title">
              {{ $t('users.sessionList') }}
              <span class="ov-count">{{ createdUsers.length }}</span>
            </div>
          </div>
        </div>

        <div v-if="createdUsers.length === 0" class="ov-card__body">
          <div class="ov-empty">
            <div class="cau-empty__title">{{ $t('users.noUsers') }}</div>
            <div>{{ $t('users.noUsersSub') }}</div>
          </div>
        </div>

        <v-data-table
          v-else
          :headers="createdUsersHeaders"
          :items="createdUsers"
          class="ov-table"
          hide-default-footer
          :items-per-page="-1"
        >
          <template v-slot:[`item.nom`]="{ item }">
            <div class="cau-user">
              <div class="cau-user__name">{{ item.nom }} {{ item.prenoms }}</div>
              <div class="cau-user__email">{{ item.email }}</div>
            </div>
          </template>
          <template v-slot:[`item.roleCode`]="{ item }">
            <span class="ov-badge ov-badge--navy ov-badge--plain">{{ roleLabel(item.roleCode) }}</span>
            <div v-if="item.gareDesignation" class="cau-user__email mt-1">{{ item.gareDesignation }}</div>
          </template>
          <template v-slot:[`item.actions`]="{ index }">
            <div class="ov-row-actions">
              <button
                type="button"
                class="ov-icon-btn"
                :class="{ 'cau-editing': editingIndex === index }"
                :title="$t('users.editUser')"
                @click="editUser(index)"
              >
                <v-icon small>mdi-pencil-outline</v-icon>
              </button>
              <button
                type="button"
                class="ov-icon-btn ov-icon-btn--danger"
                :title="$t('users.deleteUser')"
                @click="deleteUser(index)"
              >
                <v-icon small>mdi-trash-can-outline</v-icon>
              </button>
            </div>
          </template>
        </v-data-table>

        <div v-if="createdUsers.length > 0" class="ov-card__foot">
          <button type="button" class="ov-btn ov-btn--primary" @click="createAllAccountsApi">
            <v-icon small>mdi-check</v-icon>
            {{ $t('users.createAccountsBtn') }} ({{ createdUsers.length }})
          </button>
        </div>
      </div>
    </div>

    <v-alert class="myalert alert-success" type="success" width="350px" dismissible>{{ successMsg }}</v-alert>
    <v-alert class="myalert alert-error" type="error" width="350px" dismissible>{{ errorMsg }}</v-alert>
    <v-overlay :value="overlay"><v-progress-circular indeterminate size="56" color="#159e72"></v-progress-circular></v-overlay>
  </div>
</template>

<script>
import {
  ROLE_ADMIN_COMPAGNIE_TRANSPORT,
  ROLE_ADMIN_SOCIETE_MERE,
  ROLE_UTI_SIMPLE,
} from "@/components/globalConfig/constUsersRoles";
import axios from "axios";
import {
  getStationApi,
  getUserRoleApi,
} from "@/modules/common/services/commonApi";
import {
  showErrorMessage,
  showSuccessMessage,
} from "@/modules/messages/messageProcess";
import {
  API_CREATE_USER,
  API_OBTENIR_LISTE_DES_DEMANDES_ADHESIONS_VALIDEES,
  HEADERS,
} from "@/components/globalConfig/globalConstConfig";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default {
  name: "CreateAccountUser.vue",
  data() {
    return {
      errorMsg: null,
      successMsg: null,
      passwordIsVisible: false,
      confirmPasswordIsVisible: false,
      userRoleCode: null,
      confirmPassword: null,
      submitted: false,

      compagnieTransportList: [],
      compagnieTransportRaisonSociale: null,
      gareTransport: null,
      options: { index: 0, size: 8 },
      overlay: false,

      userAccount: {
        nom: null,
        prenoms: null,
        password: null,
        login: null,
        email: null,
        compagnieTransportRaisonSociale: null,
        gareDesignation: null,
        roleCode: null,
      },

      stationData: {
        data: { compagnieTransportRaisonSociale: null },
      },

      accountDataToSend: { datas: [] },

      roleList: [],
      stations: [],
      userRoleToShow: [],

      editingIndex: -1,
      createdUsers: [],
      createdUsersHeaders: [
        { text: 'Utilisateur', value: 'nom',      sortable: true  },
        { text: 'Login',       value: 'login',    sortable: false },
        { text: 'Rôle',        value: 'roleCode', sortable: false },
        { text: 'Actions',     value: 'actions',  sortable: false, align: 'end' },
      ],
    };
  },

  computed: {
    showCompagnieField() {
      return (
        this.$store.state.userAuthentified.roleCode === 'RoleAdminSocieteMere' &&
        ['RoleAdminCompagnieTransport', 'RoleUtiGareCompagnieTransport', 'RoleAgentGare', 'RoleUtiBagagiste'].includes(this.userRoleCode)
      );
    },
    showGareField() {
      return ['RoleUtiGareCompagnieTransport', 'RoleAgentGare', 'RoleUtiBagagiste'].includes(this.userRoleCode);
    },
    emailError() {
      if (!this.userAccount.email) return 'Champ obligatoire';
      if (!EMAIL_REGEX.test(this.userAccount.email)) return 'Format d\'email invalide (ex: john@example.com)';
      return null;
    },
    confirmPasswordError() {
      if (!this.confirmPassword) return 'Champ obligatoire';
      if (this.confirmPassword !== this.userAccount.password) return this.$t('users.passwordMismatch');
      return null;
    },
  },

  methods: {
    async getAllUserRole() {
      try {
        const userRoles = await getUserRoleApi({}, this.$store.state.userAuthentified.token);
        this.roleList = userRoles;
        if (this.$store.state.userAuthentified.roleCode === ROLE_ADMIN_SOCIETE_MERE) {
          this.roleList.forEach((element) => {
            if (element.code !== ROLE_ADMIN_SOCIETE_MERE && element.code !== ROLE_UTI_SIMPLE) {
              this.userRoleToShow.push(element);
            }
          });
        }
        if (this.$store.state.userAuthentified.roleCode === ROLE_ADMIN_COMPAGNIE_TRANSPORT) {
          this.roleList.forEach((element) => {
            if (
              element.code !== ROLE_ADMIN_COMPAGNIE_TRANSPORT &&
              element.code !== ROLE_ADMIN_SOCIETE_MERE &&
              element.code !== ROLE_UTI_SIMPLE
            ) {
              this.userRoleToShow.push(element);
            }
          });
        }
      } catch (error) {
        showErrorMessage();
      }
    },

    async getAllStations(raisonSociale) {
      this.stationData.data.compagnieTransportRaisonSociale = raisonSociale;
      try {
        const stationList = await getStationApi(this.stationData, this.$store.state.userAuthentified.token);
        this.stations = stationList;
      } catch (error) {
        this.errorMsg = error;
        showErrorMessage();
      }
    },

    async getAllStationByCompagnie() {
      if (this.$store.state.userAuthentified.roleCode == "RoleAdminCompagnieTransport") {
        this.stationData.data.compagnieTransportRaisonSociale =
          this.$store.state.userAuthentified.compagnieTransportRaisonSociale;
        try {
          const stationList = await getStationApi(this.stationData, this.$store.state.userAuthentified.token);
          this.stations = stationList;
        } catch (error) {
          this.errorMsg = error;
          showErrorMessage();
        }
      }
    },

    // LIBELLÉ LISIBLE D'UN RÔLE À PARTIR DE SON CODE
    roleLabel(code) {
      const role = this.roleList.find((r) => r.code === code);
      return role ? role.libelle : code;
    },

    addToList() {
      this.submitted = true;

      // Vérifications champs de base
      if (!this.userAccount.nom || !this.userAccount.prenoms ||
          !this.userAccount.login || !this.userAccount.password) {
        this.errorMsg = this.$t('users.fillAllFields');
        showErrorMessage();
        return;
      }

      // Validation email
      if (this.emailError) {
        this.errorMsg = this.emailError;
        showErrorMessage();
        return;
      }

      // Confirmation mot de passe
      if (this.confirmPasswordError) {
        this.errorMsg = this.confirmPasswordError;
        showErrorMessage();
        return;
      }

      // Rôle
      if (!this.userRoleCode) {
        this.errorMsg = this.$t('users.selectRoleError');
        showErrorMessage();
        return;
      }

      // Compagnie si nécessaire
      if (this.showCompagnieField && !this.compagnieTransportRaisonSociale) {
        this.errorMsg = this.$t('users.selectCompanyError');
        showErrorMessage();
        return;
      }

      // Gare si nécessaire
      if (this.showGareField && !this.gareTransport) {
        this.errorMsg = this.$t('users.selectStationError');
        showErrorMessage();
        return;
      }

      const entry = {
        nom: this.userAccount.nom,
        prenoms: this.userAccount.prenoms,
        login: this.userAccount.login,
        email: this.userAccount.email,
        password: this.userAccount.password,
        roleCode: this.userRoleCode,
        compagnieTransportRaisonSociale: this.compagnieTransportRaisonSociale,
        gareDesignation: this.gareTransport,
      };

      if (this.editingIndex >= 0) {
        this.$set(this.createdUsers, this.editingIndex, entry);
        this.editingIndex = -1;
      } else {
        this.createdUsers.push(entry);
      }

      this.reinintialiseForm();
    },

    editUser(index) {
      const u = this.createdUsers[index];
      this.userAccount.nom = u.nom;
      this.userAccount.prenoms = u.prenoms;
      this.userAccount.login = u.login;
      this.userAccount.email = u.email;
      this.userAccount.password = u.password;
      this.confirmPassword = u.password;
      this.userRoleCode = u.roleCode;
      this.compagnieTransportRaisonSociale = u.compagnieTransportRaisonSociale;
      this.gareTransport = u.gareDesignation;
      this.editingIndex = index;
      this.submitted = false;
    },

    deleteUser(index) {
      this.createdUsers.splice(index, 1);
      if (this.editingIndex === index) {
        this.editingIndex = -1;
        this.reinintialiseForm();
      }
    },

    async createAllAccountsApi() {
      if (this.createdUsers.length === 0) return;
      this.overlay = true;
      let successCount = 0;
      for (const user of this.createdUsers) {
        try {
          const body = { datas: [{ ...user }] };
          const response = await axios.post(API_CREATE_USER, body, {
            headers: HEADERS(this.$store.state.userAuthentified.token),
          });
          if (response.status == 200 && response.data.status.code == 800) {
            successCount++;
          } else {
            this.errorMsg = response.data.status.message || this.$t('users.creationError');
            showErrorMessage();
          }
        } catch (e) {
          this.errorMsg = e;
          showErrorMessage();
        }
      }
      this.overlay = false;
      if (successCount > 0) {
        this.successMsg = `${successCount} ${this.$t('users.creationBatchSuccess')}`;
        showSuccessMessage();
        if (successCount === this.createdUsers.length) {
          this.createdUsers = [];
        } else {
          this.createdUsers = this.createdUsers.slice(successCount);
        }
      }
    },

    reinintialiseForm() {
      this.userAccount.nom = null;
      this.userAccount.prenoms = null;
      this.userAccount.login = null;
      this.userAccount.email = null;
      this.userAccount.roleCode = null;
      this.userAccount.compagnieTransportRaisonSociale = null;
      this.userAccount.password = null;
      this.confirmPassword = null;
      this.compagnieTransportRaisonSociale = null;
      this.userRoleCode = null;
      this.gareTransport = null;
      this.submitted = false;
      this.editingIndex = -1;
    },

    async getCompagnieTransportList() {
      if (this.$store.state.userAuthentified.roleCode == "RoleAdminSocieteMere") {
        await axios
          .post(API_OBTENIR_LISTE_DES_DEMANDES_ADHESIONS_VALIDEES, this.options, {
            headers: HEADERS(this.$store.state.userAuthentified.token),
          })
          .then((response) => {
            if (response.status == 200) {
              if (response.data.status.code != 800) {
                this.errorMsg = response.data.status.message;
                showErrorMessage();
              } else {
                this.compagnieTransportList = response.data.items || [];
              }
            }
          })
          .catch((e) => {
            this.errorMsg = e;
            showErrorMessage();
          });
      }
    },
  },

  mounted() {
    this.getAllUserRole();
    this.getCompagnieTransportList();
    this.getAllStationByCompagnie();
  },
};
</script>

<style lang="scss" scoped>
@import "../../../styles/admin/ui";

.cau-grid {
  display: grid;
  grid-template-columns: 400px minmax(0, 1fr);
  gap: 24px;
  align-items: start;
}

.cau-list {
  position: sticky;
  top: 88px;
}

.cau-section {
  padding-bottom: 20px;
  margin-bottom: 20px;
  border-bottom: 1px solid $ov-line;

  &:last-child {
    padding-bottom: 0;
    margin-bottom: 0;
    border-bottom: none;
  }
}

.cau-section__label {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: $ov-green;
  margin-bottom: 14px;
}

.cau-stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.cau-password {
  position: relative;

  .ov-input {
    padding-right: 44px;
  }
}

.cau-password__toggle {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: $ov-muted;
  cursor: pointer;

  .v-icon {
    color: inherit !important;
  }

  &:hover {
    background: rgba(15, 23, 42, 0.05);
  }
}

.cau-empty__title {
  font-weight: 800;
  color: $ov-navy;
  margin-bottom: 4px;
}

.cau-user__name {
  font-weight: 700;
  color: $ov-navy;
}

.cau-user__email {
  font-size: 12px;
  color: $ov-muted;
}

.cau-editing {
  color: $ov-green;
  border-color: rgba(21, 158, 114, 0.5);
  background: rgba(21, 158, 114, 0.08);
}

@media (max-width: 1100px) {
  .cau-grid {
    grid-template-columns: 1fr;
  }

  .cau-list {
    position: static;
  }
}
</style>
